import {createContext, useCallback, useContext, useEffect, useReducer} from "react";

import * as api from "../../services/api";
import {templateMapper} from "../../mapper/templateMapper";
import {useFeedbackTools} from "../../components/FeedbackTools";


const TemplatesContext = createContext();
export const useTemplates = () => useContext(TemplatesContext);

export default function TemplatesProvider({children}) {
    // context state
    const [state, dispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'createTemplate':
                    return {
                        ...state,
                        templates: [...state.templates, action.template],
                        isCreatingNewModel: false
                    };
                case 'updateTemplate':
                    return {
                        ...state,
                        templates: state.templates.map(
                            t => t.id === action.template.id ?
                                action.template :
                                (action.template.isDefault && t.type === action.template.type ? {
                                    ...t,
                                    isDefault: false
                                } : t)  // When updating the template if it is the default one set all the others to false
                        ),
                        selectedTemplate: action.template,
                        isUpdatingSelectedTemplate: false
                    }
                case 'deleteTemplate':
                    return {
                        ...state,
                        ...(state.selectedTemplateId !== action.templateId ? {} : {
                            selectedTemplateId: null,
                            selectedTemplate: {}
                        }),
                        templates: state.templates.filter(t => t.id !== action.templateId)
                    };
                default:
                    throw new Error(`Cannot find action ${action.type} for templates reducer`);
            }
        },
        {
            templates: [],
            templatesLoaded: false,
            isLoadingTemplates: false,
            selectedTemplate: {},
            selectedTemplateId: null,
            isLoadingSelectedTemplate: false,
            isUpdatingSelectedTemplate: false,
            isCreatingNewModel: false,
            isSendingEmail: false,
        }
    );

    // Feedback tools
    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const loadTemplates = useCallback(
        async (params) => {
            dispatcher({type: "setAttributes", attributes: {isLoadingTemplates: true}})
            try {
                const templates = await api.Templates.getAllTemplates(params);
                dispatcher({
                    type: "setAttributes", attributes: {
                        templates: templates.map(t => templateMapper(t)),
                        templatesLoaded: true,
                        isLoadingTemplates: false
                    }
                })
                return templates;
            } catch (e) {
                showErrorNotification(e);
                dispatcher({type: "setAttributes", attributes: {isLoadingTemplates: false}})
            }
            return [];
        },

        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const getTemplate = useCallback(
        async (templateId) => {
            if (templateId != null) {
                const template = await api.Templates.getTemplate(templateId);
                return templateMapper(template);
            } else {
                throw Error(`Expected a valid templateId got ${templateId}`);
            }
        },
        []
    );

    const createTemplate = useCallback(
        async (params) => {
            try {
                dispatcher({type: "setAttributes", attributes: {isCreatingNewModel: true} });
                const data = await api.Templates.createTemplate(params);
                const template = templateMapper(data);
                dispatcher({type: "createTemplate", template: template});
                return template;
            } catch (e) {
                showErrorNotification(e);
            }
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const updateTemplate = useCallback(
        async (templateId, params) => {
            dispatcher({type: "setAttributes", attributes: {isUpdatingSelectedTemplate: true} });
            const template = await api.Templates.updateTemplate(templateId, params);
            dispatcher({type: "updateTemplate", template: template});
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const deleteTemplate = useCallback(
        async (templateId) => {
            try {
                await api.Templates.deleteTemplate(templateId);
                dispatcher({type: "deleteTemplate", templateId: templateId});
                showSuccessNotification("Modello email eliminato");
            } catch (e) {
                showErrorNotification(e);
            }
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const createTemplateEmailRendering = useCallback(
        async (templateId, params = {}) => {
            if (templateId != null) {
                return await api.Templates.createTemplateEmailRendering(templateId, params);
            } else {
                throw Error(`Expected a valid templateId got ${templateId}`);
            }
        },
        []
    );

    const sendTemplateEmail = useCallback(
        async (templateId, params) => {
            if (templateId != null) {
                if (state.isSendingEmail === false) {
                    dispatcher({type: "setAttributes", attributes: {isSendingEmail: true}});
                }
                try {
                    const emailMessage = await api.Templates.sendTemplateEmail(templateId, params);
                    dispatcher({type: "setAttributes", attributes: {isSendingEmail: false}});
                    return emailMessage;
                } catch (e) {
                    dispatcher({type: "setAttributes", attributes: {isSendingEmail: false}});
                    throw e
                }
            } else {
                throw Error(`Expected a valid templateId got ${templateId}`);
            }
        },
        [state.isSendingEmail]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleTemplateSelection = useCallback(
        (templateId) => dispatcher({type: 'setAttributes', attributes: {selectedTemplateId: templateId}}),
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Load the selected template.
    // Do not call the API when the selectedTemplateId is null or when it is equal to the currently selected template id.
    // This might happen when the selected template is updated and the use effect is changed because the templates array
    // changes.
    const loadSelectedTemplate = useCallback(
        async () => {
            // Load the template
            if (state.selectedTemplateId !== null && state.selectedTemplateId !== state.selectedTemplate.id) {
                dispatcher({type: 'setAttributes', attributes: {isLoadingSelectedTemplate: true}});
                try {
                    const template = await getTemplate(state.selectedTemplateId);
                    dispatcher({
                            type: 'setAttributes', attributes:
                                {isLoadingSelectedTemplate: false, selectedTemplate: template}
                        }
                    );
                } catch (e) {
                    showErrorNotification(e);
                    dispatcher({type: "setAttributes", attributes: {isLoadingSelectedTemplate: false}});
                }
            }
        },
        [getTemplate, state.selectedTemplateId, state.templates]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(
        () => {
            loadSelectedTemplate();
        },
        [loadSelectedTemplate]
    );

    // Select the default template
    useEffect(
        () => {
            if (state.selectedTemplateId === null && state.templates.length > 0) {
                const template = state.templates.find(t => t.isDefault) || state.templates[0]
                dispatcher({type: 'setAttributes', attributes: {selectedTemplateId: template.id}});
            }
        },
        [state.selectedTemplateId, state.templates]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const context = {
        templates: state.templates,
        templatesLoaded: state.templatesLoaded,
        isLoadingTemplates: state.isLoadingTemplates,
        selectedTemplate: state.selectedTemplate,
        selectedTemplateId: state.selectedTemplateId,
        isSendingEmail: state.isSendingEmail,
        isLoadingSelectedTemplate: state.isLoadingSelectedTemplate,
        isUpdatingSelectedTemplate: state.isUpdatingSelectedTemplate,
        isCreatingNewModel: state.isCreatingNewModel,
        loadTemplates,
        getTemplate,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        createTemplateEmailRendering,
        sendTemplateEmail,
        handleTemplateSelection
    };

    return <TemplatesContext.Provider value={context}>
        {children}
    </TemplatesContext.Provider>
}